import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Spin,
  Typography,
  Layout,
  message,
  Divider,
  Grid,
  Space,
} from 'antd';
import styled from 'styled-components';
import { SearchOutlined } from '@ant-design/icons';
import { onError } from 'utils/handlers';
import { DATETIME_FRONTEND_FORMAT, formatReceived } from 'utils/formatValues';
import Previewer from 'components/Previewer';
import useQuery from 'hooks/useQuery';
import { useHistory } from 'react-router';
import { toUpper } from 'utils/normalizers';
import { getConstanciasDeContribuyentes } from 'api/empresas';

const ENV_NAME = process.env.REACT_APP_ENV_NAME || 'migob';
// eslint-disable-next-line import/no-dynamic-require
const bg = require(`../../assets/${ENV_NAME}/bg.png`);

const ValidacionDeConstancias = () => {
  const history = useHistory();

  const [folio] = useQuery(['folio']);

  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [base64, setBase64] = useState();
  const [constancia, setConstancia] = useState(null);

  const onFinish = async (_folio) => {
    try {
      setLoading(true);
      await form.validateFields();
      const params = form.getFieldsValue();
      if (_folio) {
        params.folio_documento = _folio;
      }
      if (!params.folio_documento) {
        message.warn('Ingrese un folio válido');
      } else {
        const [_constancia] = await getConstanciasDeContribuyentes(params);
        if (_constancia) {
          setConstancia(formatReceived({
            ..._constancia,
            nombre_razon: _constancia?.contribuyente?.razon_social
            ?? _constancia?.contribuyente?.nombre_completo,
          }));
          setBase64(_constancia.imagenb64);
          form.resetFields();
        } else {
          message.warn('No se encontraron resultados');
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  useEffect(() => {
    if (folio) {
      form.setFieldsValue({ folio });
      onFinish(folio);
    }
  }, []);

  const onCancel = () => {
    form.resetFields();
    setConstancia();
    setBase64();
    history.replace({
      search: '',
    });
  };

  return (
    <Spin spinning={loading} tip="Cargando...">
      <Content style={{ backgroundPosition: screens.md ? 'center 0' : '' }}>
        <Row justify="center" align="middle" style={{ height: '100vh' }}>
          <Card xs={22} sm={22} md={18} lg={14} xl={12} style={{ height: 'auto' }}>
            <CardHeader>
              <Typography.Title level={4}>
                {`${constancia ? 'Información' : 'Búsqueda'} de constancia`}
              </Typography.Title>
            </CardHeader>
            <CardContent style={{ paddingLeft: screens.md ? '10vw' : 15, paddingRight: screens.md ? '10vw' : 15 }}>
              {constancia ? (
                <Row justify="space-between" className="info">
                  <Col span={11}>
                    <Label>Folio:</Label>
                    <Value>{constancia?.folio_documento ?? ''}</Value>
                  </Col>
                  {screens.md && <Divider type="vertical" />}
                  <Col span={11} style={{ textAlign: 'right' }}>
                    <Label>Fecha:</Label>
                    <Value>{constancia?.fecha_emision?.format(DATETIME_FRONTEND_FORMAT)}</Value>
                  </Col>
                  {screens.md && <Divider />}
                  <Col span={11}>
                    <Label>R.F.C:</Label>
                    <Value>{constancia?.contribuyente?.rfc}</Value>
                  </Col>
                  {screens.md && <Divider type="vertical" />}
                  <Col span={11} style={{ textAlign: 'right' }}>
                    <Label>Nombre o Razón social:</Label>
                    <Value>{constancia?.nombre_razon}</Value>
                  </Col>
                  {screens.md && <Divider />}
                  <Col span={11}>
                    <Label>Tipo de documento:</Label>
                    <Value>{constancia?.tipo_documento_contribuyente?.descripcion}</Value>
                  </Col>
                  {screens.md && <Divider type="vertical" />}
                  <Col span={11} style={{ textAlign: 'right' }}>
                    <Label>Estado:</Label>
                    <Value>{constancia?.estado_de_constancia?.descripcion}</Value>
                  </Col>
                  <Space style={{ margin: '20px auto' }} size={20}>
                    <Button
                      style={{
                        borderColor: '#60212E',
                        borderRadius: 8,
                        padding: '0 10px',
                      }}
                      onClick={onCancel}
                    >
                      Buscar otro
                    </Button>
                    <Button
                      style={{
                        color: '#FFFFFF',
                        backgroundColor: '#60212E',
                        borderRadius: 8,
                        padding: '0 10px',
                      }}
                      onClick={() => setVisiblePreview(true)}
                    >
                      Detalle Completo
                    </Button>
                  </Space>
                  <br />
                </Row>
              ) : (
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={() => onFinish()}
                >
                  <Row gutter={[10, 10]}>
                    <Col span={24}>
                      <Form.Item label="Folio" name="folio_documento" normalize={toUpper}>
                        <Input allowClear />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item>
                        <Button htmlType="submit" block icon={<SearchOutlined />}>
                          Buscar
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              )}
            </CardContent>
          </Card>
        </Row>
      </Content>
      <Previewer
        visible={visiblePreview}
        setVisible={setVisiblePreview}
        base64={base64}
      />
    </Spin>
  );
};

const Content = styled(Layout.Content)`
  background-size: cover;
  background-image: url(${bg});
  min-height: 100vh;
`;

const CardHeader = styled.div`
  background-color: #5D575B;
  padding: 15px 0;
  text-align: center;
  .ant-typography {
    color: #FFFFFF;
  }
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const Card = styled(Col)`
  background-color: #FFFFFF;
  border-radius: 8px;
`;

const CardContent = styled.div`
  padding-top: 15px;
  padding-bottom: 15px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  .info .ant-col {
    margin: 10px 0;
  }
  .ant-divider {
    margin: 0;
    border-top: 1px solid #DDDDDD;
  }
  .ant-divider-vertical {
    border-left: 1px solid #DDDDDD;
    height: calc(4em - 10px);
    margin: 5px;
  }
`;

const Label = styled(Typography.Text)`
  color: #8D8D8D;
  size: 13px;
  display: block;
`;

const Value = styled(Typography.Text)`
  color: #000000;
  size: 15px;
`;

export default ValidacionDeConstancias;
